/**
 * 디자인 토큰 중앙 관리
 *
 * index.css의 CSS 변수와 연동되는 TypeScript 상수 정의
 * 모든 색상, 간격, 타이포그래피, 애니메이션 등을 여기서 관리합니다.
 *
 * @example
 * import { colors, spacing, typography } from '@/styles/design-tokens';
 *
 * <div style={{ color: colors.primary, padding: spacing.md }}>
 *   Hello World
 * </div>
 */

// ============================================
// 1. 색상 토큰 (Color Tokens)
// ============================================
export const colors = {
  // 기본 색상 — 2026-10-14 워크스페이스 디자인(레퍼런스 «Tendra»). 정본 값은 styles/workspace.css 의 --ws-*
  // primary = 강조·링크·선택 표시의 남색. ⛔ 주 «단추» 는 검정이다(shadcn --primary) — 여기 값으로 단추를 칠하지 말 것
  primary: '#4f5fd8',           // 남색 — 링크·선택·정보
  primaryLight: '#8f9cf0',      // 옅은 남색 (어두운 배경용)
  primaryDark: '#3538cd',       // 진한 남색 (눌림)
  secondary: '#18181c',         // 거의 검정 — 주 단추
  destructive: '#d92d20',       // 삭제/위험 빨간색

  // 배경 색상 (차가운 회색 — workspace.css --ws-subtle·--ws-sunken·--ws-hover 와 같은 값)
  background: {
    light: '#ffffff',           // 흰색 — 카드/페이지
    page: '#f7f7f8',           // 아주 옅은 회색 — 섹션 교대 배경
    dark: '#18181c',            // 거의 검정
    muted: '#f4f4f5',          // 비활성
    accent: '#eef2ff',         // 옅은 남색 (선택·강조 바탕)
    hover: '#f4f4f6',          // 호버
    card: {
      light: '#ffffff',
      dark: '#18181c',
      elevated: '#ffffff',
    },
  },

  // 텍스트 색상 (workspace.css --ws-ink 단계)
  text: {
    primary: '#111114',           // 거의 검정
    secondary: '#5f5f69',         // 보조 글
    muted: '#9b9ba4',             // 흐린 글
    placeholder: '#9b9ba4',
    onPrimary: '#ffffff',
    onDark: '#e7e9ea',
  },

  // 테두리 색상 (Whisper Borders)
  border: {
    default: 'rgba(0,0,0,0.1)',   // Whisper Border — 미세한 경계선
    light: 'rgba(0,0,0,0.06)',    // 더 약한 whisper
    muted: 'rgba(0,0,0,0.08)',
    dark: '#18181c',
    focus: '#4f5fd8',             // 초점 남색
    hover: 'rgba(0,0,0,0.15)',
  },

  // 모달 뒤를 덮는 막 (Overlay)
  // ⛔ 여기 없으면 각 모달이 rgba 를 직접 적게 되고, 화면마다 어둡기가 달라진다.
  overlay: {
    default: 'rgba(0,0,0,0.55)',  // 뒤 내용이 비치되 읽히지는 않는 정도
  },

  // 차트 색상
  chart: {
    blue: '#4f5fd8',
    green: '#17a34a',
    yellow: '#d29b1f',
    lightGreen: '#3ccb7f',
    red: '#d92d20',
  },

  // 상태 색상 (Semantic Accent)
  status: {
    success: '#067647',         // 초록 (글자로도 읽히는 진한 값)
    warning: '#b54708',         // 호박
    error: '#d92d20',           // 빨강
    info: '#4f5fd8',            // 남색
    // HR 모듈 보강 (Phase 2 polish)
    successDark: '#128a2c',     // HR active/positive 진초록
    successBg: '#eafaec',       // HR success 배경 연초록
    warningBg: '#fff8e1',       // HR warning 배경 연노랑
    warningDark: '#a06800',     // HR warning 강조 텍스트
    dangerSoft: '#c94e6c',      // HR danger 강조(부드러운 분홍)
    dangerBg: '#fdf1f3',        // HR danger 배경 연분홍
  },

  // ── 자동화 관제 상태 색 ──────────────────────────────────────────────
  // 사용처: 자동화 태스크 화면(`/automation/tasks`).
  // 「모른다(확인 불가·꺼짐·중단됨)」를 「정상」이나 「고장」과 **다른 색**으로 두는 것이 핵심이다.
  // 기록이 없다고 초록으로 칠하면 거짓말이고, 빨강으로 칠하면 거짓 경보다.
  // stuck(멈춤 의심)만 기존 팔레트에 없는 보라를 새로 둔다 — 실패·지연과 구분되어야 한다.
  automation: {
    stuck: '#7c5cc4',        // 멈춤 의심 — 시작만 있고 끝이 없는 실행
    stuckBg: '#f1ecfb',
    stale: '#b54708',        // 무응답 — status.warning 과 같은 값
    staleBg: '#fffaeb',
    failed: '#d92d20',       // 실패 — status.error 와 같은 값
    failedBg: '#fef3f2',
    running: '#4f5fd8',      // 실행 중 — primary 와 같은 값
    runningBg: '#eef2ff',
    ok: '#067647',           // 정상 — status.success 와 같은 값
    okBg: '#ecfdf3',
    unknown: '#5f5f69',      // 확인 불가 — 판정이 불가능한 상태
    unknownBg: '#f1f1f3',
    off: '#9b9ba4',          // 꺼짐·중단됨
    offBg: '#f4f4f5',
  },

  // 사이드바 색상
  sidebar: {
    background: {
      light: '#efeff1',
      dark: '#18181c',
    },
    border: {
      light: '#e6e6ea',
      dark: '#38444d',
    },
  },

  // Badge 전용 (Notion Pill)
  badge: {
    bg: '#eef2ff',
    text: '#3538cd',
  },

  // ── 마리하우스 고객 페이지 브랜드 컬러 ────────────────────────────────
  // 사용처: 고객 노출 페이지 — 주문/배송 조회(`/track/*`), 설치일정 신청·확인
  // (`/track/install-*`), 방문홈스타일링 신청(`/homestyling-apply`).
  // - HOUSE BEIGE : 페이지/카드 배경, 부드러운 강조
  // - HOUSE GRAY  : 텍스트·헤딩·아이콘·진한 강조 (정통 마리하우스 룩)
  customer: {
    houseBeige: '#e3e0db',         // 메인 — 페이지/카드/입력 배경
    houseBeigeBg: '#f0eeea',       // 더 옅은 배경 (페이지 전체) — beige 8% lighter
    houseBeigeStrong: '#cfcbc3',   // 진한 베이지 (활성/호버/구분선) — beige 8% darker
    houseGray: '#3d3b38',          // 서브 — 텍스트·헤딩·아이콘·CTA
    houseGrayMuted: '#6f6c68',     // 보조 텍스트 (placeholder, 부가 정보)
    houseGrayStrong: '#26241f',    // 강조 텍스트 (가장 진함)
    houseOnGray: '#f7f5f1',        // gray 배경 위 텍스트
  },

  // ── 외부 서비스 브랜드 색 ─────────────────────────────────────────────
  // ⛔ 이 값들은 «우리 디자인 시스템의 색» 이 아니다. 남의 로고를 그리는 데만 쓴다.
  //    로고는 브랜드가 정한 색이 곧 식별자라서 우리 토큰으로 바꿔 칠하면 안 된다
  //    (파란 슬랙, 빨강·노랑·초록·파랑 구글 — 색이 바뀌면 그 로고가 아니다).
  // ⛔ 그렇다고 컴포넌트에 16진수를 직접 쓰면 절대규칙 #9 와 디자인 게이트에 걸린다.
  //    그래서 «색상 정본 파일» 인 여기에 모아 둔다. 화면 색으로 재사용하지 말 것.
  // 사용처: components/icons/BrandIcons.tsx 한 곳뿐이다.
  brand: {
    slack: {
      blue: '#36c5f0',
      green: '#2eb67d',
      red: '#e01e5a',
      yellow: '#ecb22e',
    },
    google: {
      blue: '#4285f4',
      green: '#34a853',
      yellow: '#fbbc05',
      red: '#ea4335',
    },
  },
} as const;

// ============================================
// 2. 간격 토큰 (Spacing Tokens) - Notion Style
// ============================================
/**
 * 노션 스타일 spacing 시스템
 * - 4px 기준: 정확한 정렬과 일관성
 * - 시각적 리듬: 4px, 8px, 12px, 16px, 24px, 32px, 48px...
 */
export const spacing = {
  xs: '4px',                    // 0.25rem - 최소 간격 (밀집된 요소)
  sm: '8px',                    // 0.5rem - 작은 간격 (관련 요소)
  md: '12px',                   // 0.75rem - 중간 간격 (기본 내부 패딩)
  base: '16px',                 // 1rem - 기본 간격 (요소 사이)
  lg: '24px',                   // 1.5rem - 큰 간격 (섹션 내부)
  xl: '32px',                   // 2rem - 매우 큰 간격 (섹션 사이)
  '2xl': '48px',                // 3rem - 초대형 간격 (주요 구분)
  '3xl': '64px',                // 4rem - 최대 간격 (페이지 레벨)
  '4xl': '96px',                // 6rem - 영웅 섹션
} as const;

// ============================================
// 3. 타이포그래피 토큰 (Typography Tokens)
// ============================================
/**
 * 글자 크기·굵기 (2026-09-10 재설계)
 *
 * ⛔ 예전 눈금은 제목 넷이 700/600/600/600 이라 **거의 같은 무게** 였다.
 *   h2(24px 600)와 h3(20px 600)를 나란히 두면 어느 쪽이 상위인지 크기로만 겨우 구분됐다.
 *   → 제목은 위로 갈수록 무겁게(800→700→600), 아래로 갈수록 가볍게 벌린다.
 *
 * ⛔ 큰 글자에는 자간을 좁힌다. 한글도 24px 을 넘어가면 기본 자간이 성기게 보인다.
 *   반대로 작은 라벨은 자간을 살짝 벌려야 뭉치지 않는다.
 *
 * ⛔ 숫자에는 tabularNums 를 쓸 것. 자릿수가 세로로 안 맞으면
 *   ₩55,400 과 ₩4,300 중 어느 쪽이 큰지 «읽어서» 비교해야 한다.
 */
export const typography = {
  // 제목 (Headings) — 무게로 층을 만든다
  heading: {
    h1: { fontSize: '32px', fontWeight: 800, lineHeight: '40px', letterSpacing: '-0.028em' },
    h2: { fontSize: '24px', fontWeight: 700, lineHeight: '32px', letterSpacing: '-0.022em' },
    h3: { fontSize: '20px', fontWeight: 700, lineHeight: '28px', letterSpacing: '-0.016em' },
    h4: { fontSize: '15px', fontWeight: 600, lineHeight: '22px', letterSpacing: '-0.008em' },
  },

  // 본문 (Body)
  body: {
    large: { fontSize: '15px', fontWeight: 400, lineHeight: '24px' },
    base: { fontSize: '13.5px', fontWeight: 400, lineHeight: '20px' },
    small: { fontSize: '12px', fontWeight: 400, lineHeight: '17px' },
  },

  // 버튼/레이블 (Labels) — 작을수록 자간을 벌려 뭉침을 막는다
  label: {
    large: { fontSize: '14px', fontWeight: 600, lineHeight: '20px' },
    base: { fontSize: '13px', fontWeight: 600, lineHeight: '18px' },
    small: { fontSize: '11.5px', fontWeight: 600, lineHeight: '16px', letterSpacing: '0.01em' },
  },

  /**
   * 숫자를 나란히 세울 때 — 표의 수량·금액 칸, 지표 카드.
   * 펴 바르듯 아무 데나 쓰지 말 것. 글에 섞인 숫자까지 등폭이면 오히려 어색하다.
   */
  tabularNums: { fontVariantNumeric: 'tabular-nums' } as const,

  // 폰트 패밀리
  //   ⛔ 'Pretendard Variable' 이 먼저다 — index.html 이 불러오는 것이 가변 글꼴이다.
  //     그냥 'Pretendard' 만 적으면 (설치돼 있지 않은 한) 못 찾고 시스템 글꼴로 떨어진다.
  fontFamily: {
    sans: "'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', system-ui, sans-serif",
    mono: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace",
    serif: 'Georgia, serif',
  },
} as const;

// ============================================
// 4. 그림자 토큰 (Shadow Tokens) - Notion Style
// ============================================
/**
 * 노션 스타일 그림자 시스템
 * - 2-layer shadows: 부드럽고 입체적인 depth 표현
 * - 낮은 opacity: 과하지 않은 자연스러운 그림자
 * - 블러 처리: 부드러운 경계로 세련된 느낌
 */
/**
 * 그림자 (2026-09-10 재설계)
 *
 * ⛔ 순검정 그림자를 쓰지 않는다. 이 화면의 바탕은 **따뜻한 회백색**(#f6f5f4)인데
 *   그 위에 순검정 그늘을 두면 회색이 차갑게 뜨고 «종이 위 물체» 가 아니라
 *   «화면에 붙인 스티커» 처럼 보인다. 바탕과 같은 계열(#31302e)로 그늘을 만든다.
 *
 * ⛔ 빛의 방향을 하나로 둔다 — 전부 위에서 아래로. 어떤 카드는 y 오프셋이 있고
 *   어떤 카드는 없으면 같은 화면 안에서 조명이 두 개인 것처럼 보인다.
 */
const INK = '49,48,46';   // 바탕과 같은 계열의 따뜻한 먹색

export const shadows = {
  none: 'none',

  // Level 1: 테두리 대용 — 선 하나 값어치
  subtle: `0 0 0 1px rgba(${INK},0.10)`,

  // Level 1.5: 살짝 뜬 것
  sm: `0 1px 2px rgba(${INK},0.05), 0 1px 3px rgba(${INK},0.04)`,

  // Level 2: 카드
  base: `0 1px 2px rgba(${INK},0.04), 0 4px 12px rgba(${INK},0.06), 0 12px 28px -12px rgba(${INK},0.10)`,

  // Level 2.5: 중간 깊이
  md: `0 1px 2px rgba(${INK},0.05), 0 6px 16px rgba(${INK},0.07), 0 18px 36px -16px rgba(${INK},0.12)`,

  // Level 3: 떠 있는 것 — 팝오버·드롭다운
  lg: `0 1px 3px rgba(${INK},0.06), 0 10px 24px rgba(${INK},0.09), 0 28px 56px -20px rgba(${INK},0.18)`,

  // Level 4: 모달/오버레이 — 뒤가 확실히 뒤로 물러나야 한다
  xl: `0 2px 6px rgba(${INK},0.08), 0 16px 40px rgba(${INK},0.12), 0 44px 88px -28px rgba(${INK},0.26)`,

  // 특수 용도
  modal: `0 2px 6px rgba(${INK},0.08), 0 16px 40px rgba(${INK},0.12), 0 44px 88px -28px rgba(${INK},0.26)`,
  popover: `0 1px 3px rgba(${INK},0.06), 0 10px 24px rgba(${INK},0.09), 0 28px 56px -20px rgba(${INK},0.18)`,

  // 인터랙션
  hover: `0 1px 2px rgba(${INK},0.05), 0 8px 20px rgba(${INK},0.09), 0 20px 40px -18px rgba(${INK},0.14)`,
  focus: '0 0 0 2px #097fe8',
  active: `inset 0 1px 2px rgba(${INK},0.12)`,
} as const;

// ============================================
// 5. 모서리 반경 토큰 (Border Radius Tokens) - Notion Style
// ============================================
/**
 * 노션 스타일 border-radius 시스템
 * - 부드러운 곡선: 입체감과 친근함 증대
 * - 일관성: 3px 단위로 증가하는 규칙적인 스케일
 */
export const borderRadius = {
  none: '0',
  sm: '4px',                    // Micro — 버튼, 인풋, 기능 요소 (DESIGN.md)
  md: '5px',                    // Subtle — 링크, 리스트 아이템
  lg: '8px',                    // Standard — 작은 카드, 인라인 요소
  xl: '12px',                   // Comfortable — 표준 카드, 이미지 상단
  '2xl': '16px',                // Large — 히어로 카드, 피처 콘텐츠
  '3xl': '24px',                // 전체 화면 요소
  full: '9999px',               // Pill — 배지, 상태 인디케이터
} as const;

// ============================================
// 6. z-index 레이어 (Z-Index Layers)
// ============================================
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  modalBackdrop: 900,
  modal: 1000,
  popover: 1100,
  tooltip: 1200,
  toast: 1300,
} as const;

// ============================================
// 7. 애니메이션 토큰 (Animation Tokens)
// ============================================
export const animation = {
  duration: {
    fast: '150ms',              // 빠른 애니메이션
    base: '200ms',              // 기본 애니메이션
    normal: '200ms',            // 기본 애니메이션 (alias)
    slow: '300ms',              // 느린 애니메이션
    slower: '500ms',            // 매우 느린 애니메이션
  },
  timing: {
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  // Backwards compatibility: easing is an alias for timing
  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

// ============================================
// 8. 브레이크포인트 토큰 (Breakpoint Tokens)
// ============================================
export const breakpoints = {
  xs: '320px',                  // 모바일 세로
  sm: '640px',                  // 모바일 가로
  md: '768px',                  // 태블릿
  lg: '1024px',                 // 작은 데스크톱
  xl: '1280px',                 // 데스크톱
  '2xl': '1536px',              // 큰 데스크톱
} as const;

// ============================================
// 9. 컴포넌트 크기 프리셋 (Component Size Presets)
// ============================================
export const componentSizes = {
  // 버튼 크기
  button: {
    sm: { height: '32px', padding: '0 12px', fontSize: '13px' },
    md: { height: '40px', padding: '0 16px', fontSize: '14px' },
    lg: { height: '48px', padding: '0 24px', fontSize: '16px' },
  },

  // 입력 필드 크기
  input: {
    sm: { height: '32px', padding: '0 12px', fontSize: '13px' },
    md: { height: '40px', padding: '0 16px', fontSize: '14px' },
    lg: { height: '48px', padding: '0 16px', fontSize: '16px' },
  },

  // 테이블 크기
  table: {
    compact: { cellPadding: '8px', fontSize: '12px', rowHeight: '36px' },
    normal: { cellPadding: '12px', fontSize: '14px', rowHeight: '48px' },
    comfortable: { cellPadding: '16px', fontSize: '14px', rowHeight: '56px' },
  },

  // 모달 크기
  modal: {
    small: '400px',
    medium: '600px',
    large: '800px',
    xlarge: '1000px',
  },

  // 아이콘 박스 — 아이콘을 감싸는 정사각 컨테이너(메타 셀·리스트 어피던스 등)
  //   아이콘 자체 크기(lucide `size`)가 아니라 **배경이 깔리는 상자**의 변 길이다.
  iconBox: {
    sm: '24px',                 // 조밀한 목록 행
    md: '28px',                 // 기본 — 메타 정보 셀
    lg: '32px',                 // 강조 카드 헤더
  },

  // 진행 표시줄(체크리스트 진척도 등)
  progressBar: {
    height: '6px',
  },
} as const;

// ============================================
// 10. 유틸리티 함수
// ============================================

/**
 * CSS 변수로 색상 가져오기
 * @param varName CSS 변수 이름 (예: '--primary')
 * @returns CSS 변수 값
 *
 * @example
 * const primaryColor = getCSSVariable('--primary'); // '#1e9df1'
 */
export const getCSSVariable = (varName: string): string => {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
};

/**
 * 현재 테마 모드 확인
 * @returns 다크모드 여부
 *
 * @example
 * if (isDarkMode()) {
 *   console.log('다크모드 활성화');
 * }
 */
export const isDarkMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
};

/**
 * 반응형 값 가져오기
 * @param values 브레이크포인트별 값
 * @param defaultValue 기본값
 * @returns 현재 화면 크기에 맞는 값
 *
 * @example
 * const fontSize = getResponsiveValue({
 *   xs: '12px',
 *   sm: '14px',
 *   md: '16px',
 *   lg: '18px',
 * }, '14px');
 */
export const getResponsiveValue = <T>(
  values: { xs?: T; sm?: T; md?: T; lg?: T; xl?: T; '2xl'?: T },
  defaultValue: T
): T => {
  if (typeof window === 'undefined') return defaultValue;

  const width = window.innerWidth;

  if (width >= 1536 && values['2xl']) return values['2xl'];
  if (width >= 1280 && values.xl) return values.xl;
  if (width >= 1024 && values.lg) return values.lg;
  if (width >= 768 && values.md) return values.md;
  if (width >= 640 && values.sm) return values.sm;
  if (values.xs) return values.xs;

  return defaultValue;
};

/**
 * 투명도를 적용한 색상 반환
 * @param color 색상 값
 * @param opacity 투명도 (0-1)
 * @returns rgba 색상
 *
 * @example
 * const semiTransparent = withOpacity(colors.primary, 0.5); // rgba(30, 157, 241, 0.5)
 */
export const withOpacity = (color: string, opacity: number): string => {
  // hex to rgba 변환
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // 이미 rgba인 경우
  if (color.startsWith('rgba')) {
    return color.replace(/[\d.]+\)$/g, `${opacity})`);
  }

  // rgb인 경우
  if (color.startsWith('rgb')) {
    return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }

  return color;
};

/**
 * 미디어 쿼리 헬퍼
 * @param breakpoint 브레이크포인트 키
 * @returns 미디어 쿼리 문자열
 *
 * @example
 * const mediaQuery = mediaQuery('md'); // '@media (min-width: 768px)'
 */
export const mediaQuery = (breakpoint: keyof typeof breakpoints): string => {
  return `@media (min-width: ${breakpoints[breakpoint]})`;
};

// ============================================
// 위키 관계 지도 — 우주 캔버스 팔레트
// ============================================
/**
 * ⛔ 의도된 단일 테마 — «별을 보는 창» 이라 밝은 화면에서도 어둡다.
 *    화면 코드에 색 리터럴을 두지 않기 위한 정본 (CI 조항1 은 이 파일만 예외).
 */
export const wikiSky = {
  bg: '#04060c',
  ink: '#dde6f6',
  muted: '#7d8ca6',
  human: '#5fd8ea',        // 사람 확정 연결 (실선)
  evidence: '#8fb8c9',     // 근거 공유 (가는 실선)
  ai: '#e6b25e',           // AI 판정 (점선)
  node: '#a78bfa',
  nodeEmpty: '#5a6478',        // 내용 없는(빈) 문서 — 카테고리 색을 흐리게 대신
  star: '#cfe0ff',
  white: '#ffffff',
  cardBg: 'rgba(8,12,22,.94)',
  cardBorder: '#1a2333',
  cardBody: '#b9c3d8',
  cardShadow: '0 10px 34px rgba(0,0,0,.65)',
  labelShadow: 'rgba(0,0,0,.85)',
  // 카테고리별 점 색 — 어두운 밤하늘에서 서로 구분되는 밝은 색들. 카테고리 목록 순서로 배정한다.
  categoryPalette: ['#a78bfa', '#5fd8ea', '#6bd88f', '#e6c25e', '#f08fb8', '#f0965e', '#6b9bf0', '#c98ff0'],
} as const;

// ============================================
// 업무 위키 — 태그·카테고리 색 (PR-3)
// ============================================
/** 팔레트 크기. tokens.css 의 --wiki-tag-{0..7}-bg/fg 와 같이 움직인다 */
export const WIKI_TAG_PALETTE_SIZE = 8;

/** 문자열(태그 slug)을 0~7 로 — 같은 태그는 어디서든 같은 색(djb2) */
export function wikiPaletteIndex(key: string): number {
  let h = 5381;
  const s = String(key || '');
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h) % WIKI_TAG_PALETTE_SIZE;
}

/** 팔레트 인덱스 → CSS 변수 쌍. null(미분류)은 무채색(muted) */
export function wikiTagColors(index: number | null | undefined): { bg: string; fg: string } {
  if (index === null || index === undefined || Number.isNaN(index)) return { bg: 'var(--muted)', fg: 'var(--muted-foreground)' };
  const i = ((index % WIKI_TAG_PALETTE_SIZE) + WIKI_TAG_PALETTE_SIZE) % WIKI_TAG_PALETTE_SIZE;
  return { bg: `var(--wiki-tag-${i}-bg)`, fg: `var(--wiki-tag-${i}-fg)` };
}

// ============================================
// 해피콜 연창(連窓) — 묶음 색 (2026-09-16)
// ============================================
/** 연창 번호 상한. tokens.css 의 --yeonchang-{1..10}-bg/bd 및 DB CHECK 제약과 같이 움직인다 */
export const YEONCHANG_MAX = 10;

/** 화면에 쓰는 연창 이름 — 영문 코드가 사용자에게 보이지 않게 한 곳에서 만든다 */
export const yeonchangLabel = (no: number): string => `연창${no}`;

/**
 * 연창 번호 → CSS 변수 쌍. 번호와 색이 «고정» 대응이라 다른 줄을 지워도 색이 바뀌지 않는다.
 * 범위 밖·null 이면 null 을 돌려주고, 호출부는 색을 입히지 않는다.
 */
export function yeonchangColors(no: number | null | undefined): { bg: string; border: string } | null {
  if (no === null || no === undefined || !Number.isInteger(no) || no < 1 || no > YEONCHANG_MAX) return null;
  return { bg: `var(--yeonchang-${no}-bg)`, border: `var(--yeonchang-${no}-bd)` };
}

// ============================================
// 내 할 일 — 태그 색 (2026-09-28)
// ============================================
/** 태그 색 번호 상한. tokens.css 의 --todo-tag-{1..10}-bg/fg 및 DB CHECK(color BETWEEN 1 AND 10)와 같이 움직인다 */
export const TODO_TAG_COLOR_COUNT = 10;

/**
 * 태그 색 번호 → CSS 변수 쌍. 번호와 색은 «고정» 대응이라 같은 태그는 어느 화면에서나 같은 색이다.
 * 번호가 없거나 범위 밖이면(옛 서버 · DB 에 색 칸이 들어가기 전) 중립색.
 */
export function todoTagColors(color: number | null | undefined): { bg: string; fg: string } {
  if (color === null || color === undefined || !Number.isInteger(color) || color < 1 || color > TODO_TAG_COLOR_COUNT) {
    return { bg: 'var(--muted)', fg: 'var(--muted-foreground)' };
  }
  return { bg: `var(--todo-tag-${color}-bg)`, fg: `var(--todo-tag-${color}-fg)` };
}

// ============================================
// 타입 내보내기
// ============================================
export type ColorKey = keyof typeof colors;
export type SpacingKey = keyof typeof spacing;
export type BreakpointKey = keyof typeof breakpoints;
export type ComponentSizeKey = keyof typeof componentSizes;
